//Import required libraries
import * as firebase from 'firebase/app';
import * as d3 from 'd3';
import Jimp from 'jimp'

const justifiedLayoutNaive = (pictures, layoutConfig) => {
    let { viewportWidth, targetHeight, marginLeft, marginRight } = layoutConfig;
    let rows = [];
    let row = [];
    let currentWidth = 0;
    pictures.forEach((photo, index) => {
        let newHeight = targetHeight;
        let newWidth = photo.aspectRatio * targetHeight;
        if (currentWidth <= viewportWidth) {
            currentWidth += newWidth;
            row.push({...photo, newHeight, newWidth});
        } 
        if ((currentWidth > viewportWidth) 
            || 
            (pictures.length === index+1)) {
            let widthShortageRatio = viewportWidth/currentWidth;
            if (widthShortageRatio <= 1.5 && widthShortageRatio >= 0.5) {
                row = row.map((obj, index) => ({
                    ...obj,
                    finalHeight: obj.newHeight*widthShortageRatio,
                    finalWidth: obj.newWidth*widthShortageRatio-marginLeft,
                    marginLeft: marginLeft,
                    marginRight: index+1 === row.length ? marginRight : 0,
                }))
            } else {
                row = row.map(obj => ({
                    ...obj,
                    finalHeight: obj.newHeight,
                    finalWidth: obj.newWidth-marginLeft,
                    marginLeft: marginLeft,
                    marginRight: index+1 === row.length ? marginRight : 0,
                }))
            }
            rows.push(row);
            row = [];
            currentWidth = 0;
        }
        });
    return rows   
}

const isElementEvenPartlyInViewport = el => {

    let rect = el.getBoundingClientRect();

    return !(
        rect.top <= 0 &&
        rect.bottom <= 0
    )
}


const setImgDimensions = (file, fileMetaData, setFileMetaData) => {
    let myImage = new Image();
    myImage.src = URL.createObjectURL(file);
    myImage.addEventListener('load', () => 
    setFileMetaData({
        ...fileMetaData,
        width: myImage.width,
        height: myImage.height,
        aspectRatio: myImage.width/myImage.height,
    }))
}

const extractDateFirebase = ({seconds, nanoseconds, format = "%d-%b-%y"}) => {
    let date = new firebase.firestore.Timestamp(seconds, nanoseconds)
    date = date.toDate()
    return d3.timeFormat(format)(date)
}

const extractDateFile = ({timestamp, format = "%d-%b-%y"}) => {
    return d3.timeFormat(format)(timestamp)
}

const extractDateMetadataFirebase = doc => ({
    ...doc,
    shotOn: extractDateFirebase({...doc['DateTimeOriginal'], format: "%d-%b-%y"}),
    shotDateTimeOriginal: extractDateFirebase({...doc['DateTimeOriginal'], format: "%d-%b-%y %H:%M:%S"}),
    shotYear: extractDateFirebase({...doc['DateTimeOriginal'], format: "%Y"}),
    categoriesDisplay: doc['categories'].join(', '),
})

const extractDateMetadataFile = doc => ({
    ...doc,
    shotOn: extractDateFile({timestamp: doc['DateTimeOriginal'], format: "%d-%b-%y"}),
    shotDateTimeOriginal: extractDateFile({timestamp: doc['DateTimeOriginal'], format: "%d-%b-%y %H:%M:%S"}),
    shotYear: extractDateFile({timestamp: doc['DateTimeOriginal'], format: "%Y"}),
    categoriesDisplay: doc['categories'].join(', '),
})

const addDateToDocs = docs => {
    let updDocs = docs.map(doc => extractDateMetadataFirebase(doc))
    return updDocs
}

const groupByDocs = (docs, groupByCol, groupByColName, includeAll = false ) => {
    let arr = {};
    if (!includeAll) {
        docs.forEach(obj => {
            if (arr[obj[groupByCol]]) {
                arr[obj[groupByCol]] = [...arr[obj[groupByCol]], obj];
            } else {
                arr[obj[groupByCol]] = [obj]
            }
        })
    } else {
        arr['all'] = []
        docs.forEach(obj => {
            arr['all'] = [...arr['all'], obj];
        });
    }    

    //Convert object to array
    arr = Object.keys(arr).map(key => ({
        [groupByColName]: key, 
        pictures: arr[key],
        count: arr[key].length
    }))

    return arr
}

const splitDocsToArr = (docs, catSelected, yearSplitFlag = true) => {

    //Define required local variables
    let aggObj;

    //Add shotdate and shot-year
    let updDocs = addDateToDocs(docs)

    //sort images based on shotOn
    updDocs = updDocs.slice().sort((a, b) => d3.descending(a.DateTimeOriginal.seconds, b.DateTimeOriginal.seconds))

    //filter based on category selected & flag
    updDocs = updDocs.filter(obj => obj.categories.includes(catSelected.toLowerCase()))
    updDocs = updDocs.filter(obj => obj.flag === 'show')

    if (yearSplitFlag){        
        //Group by year and sort desc
        aggObj = groupByDocs(updDocs, 'shotYear', 'year')
        aggObj = aggObj.slice().sort((a, b) => d3.descending(a.year, b.year))
        return aggObj
    } else {
        //Group by year and sort desc
        aggObj = groupByDocs(updDocs, catSelected.toLowerCase(), 'year', true)
        aggObj = aggObj.slice().sort((a, b) => d3.descending(a.year, b.year))
        return aggObj
    }
}

async function resizeImage(fileMetaData) {
    // Read the image.
    const image = await Jimp.read(fileMetaData.url);
    // Resize the image to width AUTO and heigth 400.
    image.resize(Jimp.AUTO, 400);
    return image
  }

//function that takes in an array of pictures and returns the show/hide distribution
const getShowHideDist = (pictures) => {
    let cnt = {
        show: 0,
        hide: 0
    }

    pictures.forEach(obj => {
        cnt[obj.flag] = cnt[obj.flag] + 1 
    })

    return cnt
}

//Function to group by col which is an array of string values
const groupByArrCol =  (docs, groupByCol) => {

    let arr = {};
    docs.forEach(obj => {
        obj[groupByCol].map(cat => {
            if (arr[cat]) {
                arr[cat] = [...arr[cat], obj];
            } else {
                arr[cat] = [obj]
            }  
        })
    })

    //Convert object to array
    arr = Object.keys(arr).map(key => ({
        [groupByCol]: key, 
        pictures: arr[key],
        count: arr[key].length
    }))

    return arr
}

const getDocsInfo = docs => {

    //Add date to docs
    docs = addDateToDocs(docs)

    //Group by year and sort desc
    let yearArr = groupByDocs(docs, 'shotYear', 'year')
    yearArr = yearArr.map(obj => ({...obj, showHideDist: getShowHideDist(obj.pictures) }))

    //Group by category
    let catArr = groupByArrCol(docs, 'categories')
    catArr = catArr.map(obj => ({...obj, showHideDist: getShowHideDist(obj.pictures) }))

    let info = {
        total: docs.length,
        totalShow: docs.filter(obj => obj.flag === 'show').length,
        totalHide: docs.filter(obj => obj.flag === 'hide').length,
        yearArr,
        catArr,
    };
    return info
}

export { justifiedLayoutNaive, isElementEvenPartlyInViewport, setImgDimensions, 
            splitDocsToArr, addDateToDocs, groupByDocs, resizeImage, getShowHideDist,
            groupByArrCol, getDocsInfo, extractDateMetadataFile }

